import { useState, useEffect } from 'react'
import { clsx } from 'clsx'
import { useDevMode } from '../../hooks/useDevMode'
import { useChatStore } from '../../stores/chatStore'

// Frontend version - from package.json or env
const FRONTEND_VERSION = import.meta.env.VITE_APP_VERSION || '0.1.0'
const FRONTEND_BUILD = import.meta.env.VITE_BUILD_HASH || 'dev'
const FRONTEND_MODE = import.meta.env.MODE // 'development' | 'production'

interface HealthInfo {
  status: string
  service: string
  version: string
  build: string
  uptime_seconds?: number
  started_at?: string
  last_crash?: {
    reason: string
    previous_pid?: number
  }
}

// Format uptime as human-readable string
function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400)
  const hours = Math.floor((seconds % 86400) / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)

  if (days > 0) return `${days}d ${hours}h ${minutes}m`
  if (hours > 0) return `${hours}h ${minutes}m`
  return `${minutes}m`
}

// Type icons and colors for changelog sections
const SECTION_STYLES: Record<string, { icon: string; color: string; label: string }> = {
  'Added': { icon: '✦', color: 'text-emerald-600', label: 'Новое' },
  'Fixed': { icon: '✓', color: 'text-blue-600', label: 'Исправлено' },
  'Changed': { icon: '↻', color: 'text-amber-600', label: 'Изменено' },
  'Removed': { icon: '✕', color: 'text-red-500', label: 'Удалено' },
}

function ChangelogRenderer({ content }: { content: string }) {
  // Parse changelog into structured versions
  const versions: { version: string; date: string; sections: { type: string; items: string[] }[] }[] = []
  let current: typeof versions[0] | null = null
  let currentSection: { type: string; items: string[] } | null = null

  for (const line of content.split('\n')) {
    const versionMatch = line.match(/^## \[(\d+\.\d+\.\d+)\]\s*-\s*(.+)/)
    if (versionMatch) {
      if (current) versions.push(current)
      current = { version: versionMatch[1], date: versionMatch[2].trim(), sections: [] }
      currentSection = null
      continue
    }
    const sectionMatch = line.match(/^### (Added|Fixed|Changed|Removed)/)
    if (sectionMatch && current) {
      currentSection = { type: sectionMatch[1], items: [] }
      current.sections.push(currentSection)
      continue
    }
    if (line.startsWith('- ') && currentSection) {
      // Strip bold markers and technical refs for cleaner display
      const cleaned = line.slice(2).replace(/\*\*/g, '').replace(/\(`[^)]+`\)/g, '').trim()
      if (cleaned) currentSection.items.push(cleaned)
    }
  }
  if (current) versions.push(current)

  return (
    <div className="space-y-6">
      {versions.slice(0, 20).map((v) => (
        <div key={v.version}>
          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-base font-bold text-neutral-800">v{v.version}</span>
            <span className="text-xs text-neutral-400">{v.date}</span>
          </div>
          <div className="space-y-2">
            {v.sections.map((section, si) => {
              const style = SECTION_STYLES[section.type] || SECTION_STYLES.Changed
              return (
                <div key={si}>
                  <div className={`text-xs font-semibold ${style.color} mb-1 flex items-center gap-1`}>
                    <span>{style.icon}</span>
                    <span>{style.label}</span>
                  </div>
                  <ul className="space-y-0.5">
                    {section.items.map((item, ii) => (
                      <li key={ii} className="text-sm text-neutral-600 leading-relaxed pl-4 relative before:content-['·'] before:absolute before:left-1 before:text-neutral-300">
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

export default function StatusBar() {
  const [showTooltip, setShowTooltip] = useState(false)
  const [showChangelog, setShowChangelog] = useState(false)
  const { debugLevel, cycleDebugLevel, canToggleDebug } = useDevMode()
  const hasDebugErrors = useChatStore((s) => s.hasDebugErrors)
  const [changelog, setChangelog] = useState<string | null>(null)
  const [changelogLoading, setChangelogLoading] = useState(false)
  const [health, setHealth] = useState<HealthInfo | null>(null)
  const [healthLoading, setHealthLoading] = useState(false)
  const [healthError, setHealthError] = useState<string | null>(null)

  // Fetch health on mount to get current version
  useEffect(() => {
    const fetchInitialHealth = async () => {
      try {
        const apiUrl = import.meta.env.VITE_API_URL || ''
        const response = await fetch(`${apiUrl}/api/health`)
        if (response.ok) {
          const data = await response.json()
          setHealth(data)
        }
      } catch {
        // Silent fail - will show frontend version as fallback
      }
    }
    fetchInitialHealth()
  }, [])

  // Fetch changelog
  const fetchChangelog = async () => {
    if (changelog) return
    setChangelogLoading(true)
    try {
      const apiUrl = import.meta.env.VITE_API_URL || ''
      const response = await fetch(`${apiUrl}/api/changelog`)
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const data = await response.json()
      setChangelog(data.changelog)
    } catch {
      setChangelog('Failed to load changelog')
    } finally {
      setChangelogLoading(false)
    }
  }

  // Open changelog modal
  const handleAppleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowChangelog(true)
    fetchChangelog()
  }

  // Close modal on Escape + open from mobile header
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowChangelog(false)
    }
    const handleOpenChangelog = () => {
      setShowChangelog(true)
      fetchChangelog()
    }
    window.addEventListener('keydown', handleEsc)
    window.addEventListener('open-changelog', handleOpenChangelog)
    return () => {
      window.removeEventListener('keydown', handleEsc)
      window.removeEventListener('open-changelog', handleOpenChangelog)
    }
  }, [])

  // Fetch health on hover
  const fetchHealth = async () => {
    if (health) return // Already loaded

    setHealthLoading(true)
    setHealthError(null)

    try {
      const apiUrl = import.meta.env.VITE_API_URL || ''
      const response = await fetch(`${apiUrl}/api/health`)
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const data = await response.json()
      setHealth(data)
    } catch (e) {
      setHealthError(e instanceof Error ? e.message : 'Failed to fetch')
    } finally {
      setHealthLoading(false)
    }
  }

  const handleMouseEnter = () => {
    setShowTooltip(true)
    fetchHealth()
  }

  return (
    <>
    {/* Badge hidden on mobile (< 640px) to avoid blocking chat input */}
    <div className="fixed bottom-2 left-2 z-50 hidden sm:block">
      <div
        className="relative"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {/* Logo + Version Badge */}
        <div
          className={clsx(
            'flex items-center gap-1.5 px-2 py-1 rounded-full',
            'bg-white/80 backdrop-blur-sm shadow-sm border border-neutral-200/50',
            'text-xs text-neutral-600 hover:bg-white transition-colors',
            'select-none'
          )}
        >
          {canToggleDebug ? (
            <button
              onClick={(e) => {
                e.stopPropagation()
                cycleDebugLevel()
              }}
              className="text-base hover:scale-110 transition-transform cursor-pointer relative"
              title={`Debug: ${debugLevel.toUpperCase()}`}
              data-testid="debug-toggle"
              data-debug-level={debugLevel}
            >
              {debugLevel === 'off' ? '🍎' : debugLevel === 'basic' ? '🍏' : '🔵'}
              {hasDebugErrors && debugLevel !== 'off' && (
                <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
              )}
            </button>
          ) : (
            <span className="text-base" title="Janus Therapy">🍎</span>
          )}
          {/* Crash indicator — only for advanced debug, only real crashes (not clean restarts) */}
          {debugLevel === 'advanced' && health?.last_crash && health.last_crash.reason !== 'unclean_shutdown' && (
            <span
              className="text-[10px] text-amber-600 font-medium"
              title={`Previous crash: ${health.last_crash.reason} (PID ${health.last_crash.previous_pid})`}
            >
              crash
            </span>
          )}
          <button
            onClick={handleAppleClick}
            className="font-mono cursor-pointer hover:text-neutral-800 transition-colors"
            title="View Changelog"
          >
            v{health?.version || FRONTEND_VERSION}
          </button>
        </div>

        {/* Tooltip */}
        {showTooltip && (
          <div
            className={clsx(
              'absolute bottom-full left-0 mb-2 w-64',
              'bg-white rounded-lg shadow-lg border border-neutral-200',
              'p-3 text-xs',
              'animate-in fade-in slide-in-from-bottom-2 duration-200'
            )}
          >
            <h4 className="font-semibold text-neutral-800 mb-2">System Status</h4>

            {/* Frontend Info */}
            <div className="mb-2">
              <div className="text-neutral-500 mb-0.5">Frontend</div>
              <div className="flex justify-between">
                <span>Version:</span>
                <span className="font-mono">{FRONTEND_VERSION}</span>
              </div>
              <div className="flex justify-between">
                <span>Build:</span>
                <span className="font-mono">{FRONTEND_BUILD}</span>
              </div>
              <div className="flex justify-between">
                <span>Mode:</span>
                <span className={clsx(
                  'font-mono font-medium',
                  FRONTEND_MODE === 'production' ? 'text-green-600' : 'text-amber-600'
                )}>
                  {FRONTEND_MODE === 'production' ? 'PROD' : 'DEV'}
                </span>
              </div>
            </div>

            {/* Backend Info */}
            <div className="mb-2">
              <div className="text-neutral-500 mb-0.5">Backend</div>
              {healthLoading ? (
                <div className="text-neutral-400">Loading...</div>
              ) : healthError ? (
                <div className="text-red-500">Error: {healthError}</div>
              ) : health ? (
                <>
                  <div className="flex justify-between">
                    <span>Status:</span>
                    <span className={clsx(
                      'font-medium',
                      health.status === 'ok' ? 'text-green-600' : 'text-red-600'
                    )}>
                      {health.status === 'ok' ? '✓ Online' : '✗ ' + health.status}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Version:</span>
                    <span className="font-mono">{health.version}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Build:</span>
                    <span className="font-mono">{health.build}</span>
                  </div>
                  {health.uptime_seconds != null && (
                    <div className="flex justify-between">
                      <span>Uptime:</span>
                      <span className="font-mono text-green-600">{formatUptime(health.uptime_seconds)}</span>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-neutral-400">Hover to check</div>
              )}
            </div>

            {/* Service Name */}
            {health?.service && (
              <div className="pt-2 border-t border-neutral-100 text-neutral-400">
                {health.service}
              </div>
            )}
          </div>
        )}
      </div>

      </div>

      {/* Changelog Modal — outside hidden wrapper so mobile can trigger it */}
      {showChangelog && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={() => setShowChangelog(false)}
        >
          <div
            className={clsx(
              'bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden',
              'animate-in fade-in zoom-in-95 duration-200'
            )}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
              <div className="flex items-center gap-2">
                <span className="text-2xl">🍎</span>
                <h2 className="text-lg font-semibold text-neutral-800">Changelog</h2>
              </div>
              <button
                onClick={() => setShowChangelog(false)}
                className="text-neutral-400 hover:text-neutral-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-4 overflow-y-auto max-h-[60vh]">
              {changelogLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-400"></div>
                </div>
              ) : changelog ? (
                <ChangelogRenderer content={changelog} />
              ) : (
                <p className="text-neutral-500 text-center py-8">No changelog available</p>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-3 border-t border-neutral-200 bg-neutral-50 text-xs text-neutral-500">
              Version {health?.version || FRONTEND_VERSION} • Build {health?.build || FRONTEND_BUILD}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
